import * as Scrivito from 'scrivito'
import { DataItem, getDataClass } from 'scrivito'

import { buildAllTimesFilters, filterRecordsForView } from './recordsScope'
import { NO_LIMIT } from '../../const'
import { FilterValues, WeekRange } from '../../types'
import { DataClassName } from '@/Data/const'
import { Timerecord } from '@/Data/Timerecord/TimerecordDataClass'
import { ensureString } from '@/utils/ensureString'
import { getDataItem } from '@/utils/getDataItem'
import { getTimerecordDurations } from '@/utils/timerecordUtils'

export type AllTimesRecordRow = {
  date: string
  userName: string
  clientName: string
  orderName: string
  positionName: string
  description: string
  internalNote: string
  totalDuration: number
  billableDuration: number
  nonBillableDuration: number
  status: string
}

type ExportQuery = {
  appliedFilters: FilterValues
  weekRange: WeekRange
}

const selectAllRecords = ({
  appliedFilters,
  weekRange,
}: ExportQuery): DataItem[] => {
  const filters = buildAllTimesFilters(appliedFilters, weekRange)

  const records = Timerecord.all()
    .transform({ filters, order: [['recordedAt', 'desc']], ...NO_LIMIT })
    .take()

  return filterRecordsForView(records, appliedFilters)
}

const referencedName = (dataClassName: DataClassName, id: string): string => {
  if (!id) return ''
  return ensureString(
    getDataItem(getDataClass(dataClassName)!, id).dataItem?.get('name'),
  )
}

const toIsoDate = (value: unknown): string =>
  value
    ? new Intl.DateTimeFormat('en-CA').format(new Date(value as string))
    : ''

const REFERENCED_FIELDS = [
  ['userName', 'user', DataClassName.TIMERECORD_USER],
  ['clientName', 'client', DataClassName.TIMERECORD_CLIENT],
  ['orderName', 'order', DataClassName.TIMERECORD_ORDER],
  ['positionName', 'position', DataClassName.TIMERECORD_POSITION],
] as const

const toRecordRow = (dataItem: DataItem): AllTimesRecordRow => {
  const { totalDuration, billableDuration, nonBillableDuration } =
    getTimerecordDurations(dataItem)

  const referenced = REFERENCED_FIELDS.reduce(
    (acc, [rowKey, attribute, dataClassName]) => ({
      ...acc,
      [rowKey]: referencedName(
        dataClassName,
        ensureString(dataItem.get(attribute)),
      ),
    }),
    {} as Pick<
      AllTimesRecordRow,
      'userName' | 'clientName' | 'orderName' | 'positionName'
    >,
  )

  return {
    date: toIsoDate(dataItem.get('recordedAt')),
    ...referenced,
    description: ensureString(dataItem.get('description')),
    internalNote: ensureString(dataItem.get('internalNote')),
    totalDuration,
    billableDuration,
    nonBillableDuration,
    status: ensureString(dataItem.get('status')),
  }
}

export const loadAllTimeRecords = async (
  query: ExportQuery,
): Promise<AllTimesRecordRow[]> => {
  const records = await Scrivito.load(() => selectAllRecords(query))
  return records.map(toRecordRow)
}
